<!--
  PromptTable.vue
  프롬프트 목록 테이블 컴포넌트
-->
<template>
  <div>
    <!-- 테이블 상단 정보 및 액션 영역 -->
    <div class="table-header">
      <div class="table-info">
        <span class="result-count">전체 {{ pagination.total }}건</span>
      </div>
      <div class="table-actions">
        <a-button 
          type="primary" 
          class="yellow-button"
          @click="handleAdd"
        >
          <i class="fa fa-plus" style="margin-right: 5px"></i>프롬프트 추가
        </a-button>
      </div>
    </div>
    
    <!-- 프롬프트 목록 -->
    <div class="table-container">
      <a-table
        :columns="columns"
        :data-source="promptList"
        :loading="loading"
        :pagination="{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`,
          pageSizeOptions: ['10', '20', '50', '100'],
          showLessItems: false
        }"
        @change="handleTableChange"
        row-key="id"
      >
        <template #bodyCell="{ column, record, index }">
          <template v-if="column.key === 'index'">
            {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
          </template>
          
          <template v-else-if="column.key === 'action'">
            <div class="action-buttons">
              <a-button size="small" @click="handleEdit(record)">
                <i class="fa fa-edit" style="margin-right: 4px"></i>수정
              </a-button>
              
              <a-button size="small" @click="handleTest(record)">
                <i class="fa fa-flask" style="margin-right: 4px"></i>테스트
              </a-button>
              
              <a-popconfirm
                title="정말로 이 프롬프트를 삭제하시겠습니까?"
                @confirm="handleDelete(record.id)"
              >
                <a-button size="small" danger>
                  <i class="fa fa-trash" style="margin-right: 4px"></i>삭제
                </a-button>
              </a-popconfirm>
            </div>
          </template>
          
          <template v-else-if="column.key === 'promptType'">
            <a-tag :color="getPromptTypeColor(record.promptType)">
              {{ getPromptTypeName(record.promptType) }}
            </a-tag>
          </template>
          
          <template v-else-if="column.key === 'createAt'">
            {{ formatDate(record.createAt) }}
          </template>
          
          <template v-else-if="column.key === 'userPrompt'">
            <div class="prompt-preview">
              {{ truncateText(record.userPrompt, 50) }}
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';

export default {
  name: 'PromptTable',
  props: {
    promptList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      required: true
    }
  },
  emits: ['add', 'edit', 'delete', 'test', 'showTest', 'tableChange'],
  setup(props, { emit }) {
    // 调试信息
    console.log('PromptTable - promptList:', props.promptList);
    console.log('PromptTable - pagination:', props.pagination);
    console.log('PromptTable - loading:', props.loading);
    
    // 테이블 컬럼 정의
    const columns = [
      {
        title: '번호',
        key: 'index',
        width: 80,
        align: 'center'
      },
      {
        title: '프롬프트 코드',
        dataIndex: 'promptCode',
        key: 'promptCode',
        width: 150
      },
      {
        title: '프롬프트명',
        dataIndex: 'promptName',
        key: 'promptName',
        width: 150
      },
      {
        title: '타입',
        dataIndex: 'promptType',
        key: 'promptType',
        width: 100,
        align: 'center'
      },
      {
        title: '사용자 프롬프트',
        dataIndex: 'userPrompt',
        key: 'userPrompt',
        width: 500
      },
      {
        title: '생성일시',
        dataIndex: 'createAt',
        key: 'createAt',
        width: 150
      },
      {
        title: '작업',
        key: 'action',
        width: 200,
        align: 'center'
      }
    ];

    // 이벤트 핸들러들
    const handleAdd = () => {
      emit('add');
    };

    const handleEdit = (record) => {
      emit('edit', record);
    };

    const handleDelete = (id) => {
      emit('delete', id);
    };

    const handleTest = (record) => {
      emit('test', record);
    };

    const handleShowTest = () => {
      emit('showTest');
    };

    const handleTableChange = (pag, filters, sorter) => {
      emit('tableChange', pag, filters, sorter);
    };

    // 유틸리티 함수들
    const getPromptTypeColor = (type) => {
      const colors = {
        'medical_review': 'blue',
        'product_review': 'green',
        'service_review': 'orange'
      };
      return colors[type] || 'default';
    };

    const getPromptTypeName = (type) => {
      const names = {
        'medical_review': '의료 후기',
        'product_review': '제품 후기',
        'service_review': '서비스 후기'
      };
      return names[type] || type;
    };

    const truncateText = (text, length) => {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    };

    const formatDate = (dateString) => {
      return dayjs(dateString).format('YYYY-MM-DD HH:mm');
    };

    return {
      columns,
      handleAdd,
      handleEdit,
      handleDelete,
      handleTest,
      handleShowTest,
      handleTableChange,
      getPromptTypeColor,
      getPromptTypeName,
      truncateText,
      formatDate
    };
  }
};
</script>

<style scoped lang="scss">
// 테이블 헤더
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  width: 100%;
}

.table-info {
  .result-count {
    font-size: 14px;
    color: #1890ff;
    font-weight: 600;
  }
}

.table-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

// 테이블 컨테이너
.table-container {
  :deep(.ant-table) {
    .ant-table-thead > tr > th {
      background-color: #fafafa;
      font-weight: 600;
      color: #000;
    }
    
    .ant-table-tbody > tr:hover > td {
      background-color: #f5f5f5;
    }
  }
}

// 액션 버튼
.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
}

// 프롬프트 미리보기
.prompt-preview {
  max-width: 300px;
  word-break: break-word;
  line-height: 1.4;
}

// 버튼 스타일
:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .table-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .table-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
}
</style> 